
"use client"

import { useState, useEffect } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { readJobCriteriaFromDatabase, CriteriaOption } from "./job_criteria/databaseReader"
import { toast } from "sonner"

interface JobCriteriaProps {
  onCriteriaChange?: (values: {
    country: string
    experience: string
    site: string
    profession: string
    discipline: string
    role_type: string
    employment_type: string
  }) => void
  className?: string
}

export function JobCriteria({ onCriteriaChange, className }: JobCriteriaProps) {
  const [options, setOptions] = useState<CriteriaOption[]>([])
  const [selectedValues, setSelectedValues] = useState({
    country: '',
    experience: '',
    site: '',
    profession: '',
    discipline: '',
    role_type: '',
    employment_type: ''
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true)
      try {
        const databaseOptions = await readJobCriteriaFromDatabase()
        
        if (databaseOptions.length === 0) {
          toast.error("No job criteria data found. Please contact an administrator to upload the job criteria template.")
          return
        }
        
        setOptions(databaseOptions)
      } catch (error) {
        console.error('Error loading criteria options from database:', error)
        toast.error("Failed to load job criteria options. Please try again later.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchOptions()
  }, [])

  const getOptionsForCategory = (category: string) => {
    return options
      .filter(option => option.category === category)
      .sort((a, b) => a.value.localeCompare(b.value))
  }

  const handleSelectChange = (value: string, category: string) => {
    const newValues = {
      ...selectedValues,
      [category]: value
    }
    setSelectedValues(newValues)
    onCriteriaChange?.(newValues)
  }

  const selectStyles = "w-[70%] mx-auto"
  const customTriggerStyles = "bg-black text-white text-[11px] border-white/20 focus:ring-white/30"
  const customContentStyles = "bg-black text-[11px] border-white/20 z-50"
  const customItemStyles = "text-[11px] text-white bg-black hover:bg-white hover:text-black focus:bg-white focus:text-black"

  const categories = [
    { key: 'country', label: 'Country' },
    { key: 'experience', label: 'Experience' },
    { key: 'site', label: 'Site' },
    { key: 'profession', label: 'Profession' },
    { key: 'discipline', label: 'Discipline' },
    { key: 'role_type', label: 'Role Type' },
    { key: 'employment_type', label: 'Employment Type' }
  ]

  return (
    <div className={cn(
      "bg-secondary/50 backdrop-blur-lg border border-primary/20 rounded-lg shadow-lg shadow-primary/5 p-4 h-[450px]",
      className,
      { "border-2 border-green-400": className?.includes("border-2 border-white") }
    )}>
      <h3 className="text-lg font-semibold text-white mb-4">Step 2: Select Role Criteria</h3>
      <div className="flex flex-col gap-3">
        {categories.map(({ key, label }) => (
          <Select
            key={key}
            value={selectedValues[key as keyof typeof selectedValues]}
            onValueChange={(value) => handleSelectChange(value, key)}
            disabled={isLoading}
          >
            <SelectTrigger className={cn(selectStyles, customTriggerStyles)}>
              <SelectValue placeholder={isLoading ? "Loading..." : label} className="text-[11px]" />
            </SelectTrigger>
            <SelectContent className={customContentStyles}>
              {getOptionsForCategory(key).length > 0 ? 
                getOptionsForCategory(key).map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className={customItemStyles}
                  >
                    {option.value}
                  </SelectItem>
                )) : 
                <SelectItem value="loading" disabled className={customItemStyles}>
                  {isLoading ? "Loading..." : "No options available"}
                </SelectItem>
              }
            </SelectContent>
          </Select>
        ))}
      </div>
    </div>
  )
}
